/**
 * Maximum size of a file uploaded to an organization.
 */
export const MAX_ORGANIZATION_FILE_SIZE = 40 * 1024 * 1024;

/**
 * Path the worker serving organization files is routed on.
 */
const ORGANIZATION_FILES_PATH_PREFIX = '/files/';

/**
 * Public URL of a file uploaded to an organization.
 *
 * The files are served by a public worker rather than through the API: whatever reads them has no
 * GitBook credentials to authenticate with — a model provider fetches an attachment itself, with a
 * plain GET — so the key, which carries a UUID, is what grants access to the file.
 *
 * The host is passed in rather than read from the environment here, so this package stays free of
 * a dependency on `@gitbook/env`. Callers pass `webConfig.services.content.host`.
 */
export function getOrganizationFilePublicURL(contentHost: string, key: string): string {
    return `https://${contentHost}${ORGANIZATION_FILES_PATH_PREFIX}${key}`;
}

/**
 * The storage key a URL points at, or null when it is not an organization file URL.
 *
 * Used to recognise a file of our own before reading it, so a URL taken from a conversation cannot
 * send a request somewhere else.
 */
export function parseOrganizationFilePublicURL(contentHost: string, url: string): string | null {
    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return null;
    }

    if (parsed.protocol !== 'https:' || parsed.hostname !== contentHost) {
        return null;
    }

    if (!parsed.pathname.startsWith(ORGANIZATION_FILES_PATH_PREFIX)) {
        return null;
    }

    const key = parsed.pathname.slice(ORGANIZATION_FILES_PATH_PREFIX.length);
    return key.length > 0 ? key : null;
}

/**
 * Whether a key belongs to an organization.
 *
 * The organization is the first segment of the key, and the key is minted server-side at upload —
 * so this answers who a file belongs to without having to read it.
 */
export function isOrganizationFileKey(key: string, organizationId: string): boolean {
    return key.startsWith(`${organizationId}/`);
}
